import logo from "../splunk/logo.svg";
import { basicAuth, headerAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";

const defaultUrlPattern = ({ baseUrl }: Record<"baseUrl", string>) => {
  const pattern = exactUrlToRegex(baseUrl);
  return pattern
    ? `^${pattern.replace(/\/\.\*$/, "/services/collector(?:[/?].*)?$")}`
    : "";
};

const steps = [
  {
    title: "Create a HEC token",
    items: [
      {
        kind: "markdown",
        content:
          "In Splunk, go to **Settings > Add Data > Monitor > HTTP Event Collector** to create a token with the indexes you need. Ensure HEC and the token are enabled. Both authentication methods use this HEC token. Basic authentication uses `x` as the username and the token as the password.",
      },
    ],
  },
  {
    title: "Enter your HEC details",
    items: [
      {
        kind: "baseUrl",
        label: "HEC base URL",
        required: true,
        target: { kind: "configuration", field: "baseUrl" },
        placeholder: "https://http-inputs-yourinstance.splunkcloud.com",
        helperText:
          "Your HTTPS HEC URL, including any port or installation path, without /services/collector. Splunk Cloud usually uses port 443; Splunk Enterprise usually uses port 8088.",
      },
      {
        kind: "input",
        label: "HEC token",
        required: true,
        target: { kind: "secrets", field: "token" },
      },
    ],
  },
] as const;

const connectionTest = {
  protocol: "http",
  request: ({ baseUrl }: Record<"baseUrl", string>) => ({
    url: `${baseUrl.replace(/\/+$/, "")}/services/collector/event`,
    method: "POST" as const,
    headers: { "X-Splunk-Request-Channel": crypto.randomUUID() },
    body: "",
    // An empty request checks authentication without indexing an event.
    expectedStatus: 400,
    expectedBodyContains: "No data",
  }),
} as const;

const publicEnvVars = ({ baseUrl }: Record<"baseUrl", string>) => ({
  SPLUNK_HEC_URL: baseUrl.replace(/\/+$/, ""),
});

const usageHint = () =>
  "Use SPLUNK_HEC_URL with /services/collector/event for JSON events or /services/collector/raw for raw events. Include X-Splunk-Request-Channel with a channel GUID for raw events or tokens with indexer acknowledgment enabled.";

export const splunkHec = defineProduct({
  name: "Splunk HEC",
  tagLine: "Send events to Splunk HTTP Event Collector",
  keywords: "splunk http event collector hec logs ingest enterprise cloud",
  logo,
  tags: ["observability", "security", "data"],
  variants: [
    productVariant({
      identifier: "splunkHec",
      authLabel: "HTTP authentication",
      protocol: "http",
      defaultUrlPattern,
      auth: (app) =>
        headerAuth([
          { name: "Authorization", value: app.secret.token, scheme: "Splunk" },
        ]),
      steps,
      connectionTest,
      publicEnvVars,
      usageHint,
    }),
    productVariant({
      identifier: "splunkHecBasicAuth",
      authLabel: "Basic authentication",
      protocol: "http",
      defaultUrlPattern,
      auth: (app) => basicAuth({ username: "x", password: app.secret.token }),
      steps,
      connectionTest,
      publicEnvVars,
      usageHint,
    }),
  ],
});
